import { noop } from 'lodash';
import React from 'react';
import {
  StyleProp,
  ViewStyle,
} from 'react-native';
import { G } from 'react-native-svg';
import ObjectContentView from 'components/ObjectContentView';
import Proto from 'generated/freeform_pb';
import { mat2d, Vec2, vec2 } from 'utility/gl-matrix';
import unwrap from 'utility/unwrap';
import * as Rectangle from 'utility/Rectangle';
import * as API from 'generated/api';

const getObjectFrame = (obj: Proto.FFObject): Rectangle.Rectangle => {
  const size = obj.getSize();
  const xf = obj.getTransform()?.toObject();
  if (size == null || xf == null) {
    return Rectangle.zero();
  }

  return Rectangle.aabbByTransforming(
    Rectangle.fromXYWidthHeight({
      ...size.toObject(),
      x: 0,
      y: 0,
    }),
    mat2d.fromComponents(xf)
  );
};


interface Props {
  object: Proto.FFObject;
  creator: API.MinimalUser;
  onDragBegan: (initialDragPosition: Vec2) => void;
  onDragMoved: (dragPosition: Vec2) => void;
  onDragEnded: (dragPosition: Vec2) => void;
  style: StyleProp<ViewStyle>;
}

function ObjectView(props: Props) {
  const frame = getObjectFrame(props.object);
  const protoSize = unwrap(props.object.getSize()?.toObject());

  const transformWithinBoundingBox = mat2d.fromComponents(unwrap(props.object.getTransform()).toObject());
  mat2d.setTxTy(transformWithinBoundingBox, transformWithinBoundingBox, vec2.zero(vec2.create()));

  const transform = ((out) => {
    const centerWithinFrame = ((out) => {
      const offset = vec2.create();
      vec2.subtract(
        offset,
        vec2.scale(vec2.create(), frame.size, 0.5),
        vec2.scale(vec2.create(), vec2.fromSize(protoSize), 0.5),
      );

      mat2d.fromTranslation(out, offset);
      return out;
    })(mat2d.create());

    const centerOffset = vec2.fromSize(protoSize);
    vec2.scale(centerOffset, centerOffset, 0.5);

    const moveOriginToCenter = mat2d.fromTranslation(
      mat2d.create(),
      centerOffset,
    );
    const moveCenterToOrigin = mat2d.fromTranslation(
      mat2d.create(),
      vec2.negate(vec2.create(), centerOffset),
    );

    mat2d.mul(
      out,
      moveOriginToCenter,
      transformWithinBoundingBox,
    );
    mat2d.mul(
      out,
      out,
      moveCenterToOrigin,
    );

    mat2d.mul(
      out,
      centerWithinFrame,
      out,
    );
    return out;
  })(mat2d.create());

  return (
    <G {...Rectangle.toXYWidthHeight(frame)}>
      <G
        id={`ffobj-${props.object.getUuid()}`}
        transform={mat2d.toCSSInstruction(transform)}
        {...protoSize}
      >
        <ObjectContentView
          object={props.object}
          creator={props.creator}
        />
      </G>
    </G>
  );
}

ObjectView.defaultProps = {
  onDragBegan: noop,
  onDragMoved: noop,
  onDragEnded: noop,
  style: {},
};

export default ObjectView;
